import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, RotateCcw, AlertCircle, RefreshCw, Info } from "lucide-react";
import { api, formatApiError, formatDateTime } from "@/lib/api";

const ENTITY_META = {
  care_seekers:    { label: "Care Seekers",     color: "bg-[#DBEAFE] text-[#1E40AF]" },
  intakes:         { label: "Intakes",          color: "bg-[#E8ECE7] text-[#1E3A8A]" },
  notes:           { label: "Wellness Notes",   color: "bg-[#FEF3C7] text-[#92400E]" },
  referrals:       { label: "Referrals",        color: "bg-[#E0E7FF] text-[#3730A3]" },
  appointments:    { label: "Appointments",     color: "bg-[#DBEAFE] text-[#1E40AF]" },
  tasks:           { label: "Tasks",            color: "bg-[#E0E7FF] text-[#3730A3]" },
  maternal:        { label: "Maternal Visits",  color: "bg-[#FCE7F3] text-[#9D174D]" },
  outreach:        { label: "Outreach Events",  color: "bg-[#FEF3C7] text-[#92400E]" },
  attachments:     { label: "Attachments",      color: "bg-[#F1F5F9] text-[#334155]" },
  tickets:         { label: "Tech Tickets",     color: "bg-[#FFE4E6] text-[#9F1239]" },
  intake_requests: { label: "Inbound /refer",   color: "bg-[#E8ECE7] text-[#166534]" },
  care_plans:      { label: "Care Plans",       color: "bg-[#E0E7FF] text-[#3730A3]" },
  care_plan_goals: { label: "Care Plan Goals",  color: "bg-[#E0E7FF] text-[#3730A3]" },
  resources:       { label: "Resources",        color: "bg-[#DBEAFE] text-[#1E3A8A]" },
};

export default function RecentlyDeleted() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ total: 0, counts: {} });
  const [items, setItems] = useState([]);
  const [entity, setEntity] = useState("");      // "" = all
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(""); // id currently restoring
  const [cascadeAsked, setCascadeAsked] = useState(null); // row pending cascade prompt

  const loadCounts = async () => {
    try {
      const { data } = await api.get("/deleted-records/counts");
      setCounts(data || { total: 0, counts: {} });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadList = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (entity) params.entity = entity;
      const { data } = await api.get("/deleted-records", { params });
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCounts();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    loadList();
    // eslint-disable-next-line
  }, [entity]);

  const refreshAll = async () => { await Promise.all([loadCounts(), loadList()]); };

  const doRestore = async (row, cascade = false) => {
    setRestoring(row.id);
    try {
      const { data } = await api.post(
        `/deleted-records/${row.entity}/${row.id}/restore`,
        cascade ? { cascade: true } : {},
      );
      const casc = data?.cascaded || {};
      const cascTotal = Object.values(casc).reduce((a, b) => a + (b || 0), 0);
      toast.success(
        cascTotal > 0
          ? `Restored ${ENTITY_META[row.entity]?.label || row.entity} (+${cascTotal} related rows)`
          : `Restored: ${row.label}`,
      );
      setCascadeAsked(null);
      await refreshAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRestoring("");
    }
  };

  const handleRestoreClick = (row) => {
    if (row.entity === "care_seekers") {
      // Ask whether to cascade-restore the child rows
      setCascadeAsked(row);
      return;
    }
    doRestore(row, false);
  };

  const visibleCount = items.length;
  const entityOrder = useMemo(
    () => Object.keys(ENTITY_META).sort((a, b) => (counts.counts?.[b] || 0) - (counts.counts?.[a] || 0)),
    [counts],
  );

  return (
    <div className="space-y-6" data-testid="recently-deleted-page">
      {/* Hero */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="hw-eyebrow mb-2">Admin · Trash</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">
            Recently Deleted
          </h1>
          <p className="text-[#334155] mt-2 max-w-2xl">
            Soft-deleted records across HarborWell Connect. Rows are hidden from
            day-to-day views but kept in MongoDB for HIPAA-compliant recovery.
            Click <span className="font-medium text-[#1E3A8A]">Restore</span> to
            bring any item back.
          </p>
        </div>
        <button
          onClick={refreshAll}
          data-testid="rd-refresh-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-[#E5E5E0] text-sm font-medium text-[#1E3A8A] hover:bg-[#F9F8F6]"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Counts strip */}
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-4">
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={() => setEntity("")}
            data-testid="rd-filter-all"
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
              entity === ""
                ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                : "bg-[#F9F8F6] text-[#1E3A8A] border-[#E5E5E0] hover:bg-white"
            }`}
          >
            All · {counts.total || 0}
          </button>
          {entityOrder.map((slug) => {
            const n = counts.counts?.[slug] || 0;
            if (!n && entity !== slug) return null;
            const meta = ENTITY_META[slug];
            return (
              <button
                key={slug}
                onClick={() => setEntity(slug)}
                data-testid={`rd-filter-${slug}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                  entity === slug
                    ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                    : `${meta.color} border-transparent hover:border-[#E5E5E0]`
                }`}
              >
                {meta.label} · {n}
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>
      ) : visibleCount === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-12 text-center" data-testid="rd-empty">
          <Trash2 className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">Nothing in the trash</h3>
          <p className="text-sm text-[#334155] mt-2">
            {entity
              ? `No soft-deleted ${ENTITY_META[entity]?.label || entity} right now.`
              : "No records have been soft-deleted yet."}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
          <table className="w-full text-sm" data-testid="rd-table">
            <thead className="bg-[#F9F8F6] border-b border-[#E5E5E0]">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Type</th>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Item</th>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider hidden md:table-cell">Deleted by</th>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider whitespace-nowrap">When</th>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider hidden lg:table-cell">Reason</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const meta = ENTITY_META[r.entity] || { label: r.entity_label || r.entity, color: "bg-[#F1F5F9] text-[#334155]" };
                return (
                  <tr key={`${r.entity}:${r.id}`} className="border-t border-[#F0EFEB]" data-testid={`rd-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-[10px] font-medium uppercase tracking-wider ${meta.color}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-[#0F172A] font-medium text-sm break-all">{r.label || "—"}</div>
                      {r.extra?.care_seeker_id && (
                        <button
                          onClick={() => navigate(`/care-seekers/${r.extra.care_seeker_id}`)}
                          className="text-xs text-[#1E3A8A] underline mt-1"
                          data-testid={`rd-open-parent-${r.id}`}
                        >
                          View parent Care Seeker
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#334155] hidden md:table-cell whitespace-nowrap">
                      {r.deleted_by_name || "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#334155] whitespace-nowrap">
                      {r.deleted_at ? formatDateTime(r.deleted_at) : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#1E3A8A] hidden lg:table-cell max-w-xs">
                      <div className="truncate" title={r.deleted_reason || ""}>
                        {r.deleted_reason || <span className="text-[#0F172A]/40">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleRestoreClick(r)}
                        disabled={restoring === r.id}
                        data-testid={`rd-restore-btn-${r.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs font-medium hover:bg-[#172554] disabled:opacity-50"
                      >
                        <RotateCcw className="w-3 h-3" />
                        {restoring === r.id ? "Restoring…" : "Restore"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-4 py-3 bg-[#F9F8F6] border-t border-[#E5E5E0] text-xs text-[#1E3A8A] flex items-center gap-2">
            <Info className="w-3.5 h-3.5" />
            Showing {visibleCount} record{visibleCount === 1 ? "" : "s"}{entity ? ` · ${ENTITY_META[entity]?.label || entity}` : " · all entities"}.
          </div>
        </div>
      )}

      {/* Care Seeker cascade-restore prompt */}
      {cascadeAsked && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="rd-cascade-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-[#1E3A8A] flex-shrink-0" />
              <div>
                <h3 className="font-display text-lg font-semibold text-[#0F172A]">Restore related records?</h3>
                <p className="text-sm text-[#334155] mt-2">
                  <span className="font-medium">{cascadeAsked.label}</span> was deleted along with their
                  intakes, notes, referrals, appointments, tasks, and other linked records.
                </p>
                <p className="text-sm text-[#334155] mt-2">
                  Would you like to restore everything together, or just the Care Seeker profile?
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-6">
              <button
                onClick={() => setCascadeAsked(null)}
                className="px-4 py-2 rounded-full border border-[#E5E5E0] text-sm font-medium text-[#334155] hover:bg-[#F9F8F6]"
                data-testid="rd-cascade-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => doRestore(cascadeAsked, false)}
                disabled={!!restoring}
                className="px-4 py-2 rounded-full bg-white border border-[#1E3A8A] text-sm font-medium text-[#1E3A8A] hover:bg-[#F9F8F6] disabled:opacity-50"
                data-testid="rd-cascade-only-cs"
              >
                Care Seeker only
              </button>
              <button
                onClick={() => doRestore(cascadeAsked, true)}
                disabled={!!restoring}
                className="px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-50"
                data-testid="rd-cascade-full"
              >
                Restore everything
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
